import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  GoalAchievementPopup,
  type GoalAchievementPopupProps,
} from "./goal-achievement-popup";
import {
  fireConfetti,
  playBigWinSound,
  playGoalSound,
  playWinSound,
} from "./sound-utils";
import type {
  GamificationMetricKey,
  GamificationPeriodKey,
  GamificationSnapshot,
} from "./types";
import { METRIC_LABELS } from "./types";

const METRIC_KEYS: GamificationMetricKey[] = ["activity", "pipeline", "wins"];
const PERIOD_KEYS: GamificationPeriodKey[] = ["daily", "weekly", "monthly"];

const DAILY_GOAL_HIT_MESSAGES: Record<GamificationMetricKey, string> = {
  activity: "Daily activity goal hit — keep the momentum going!",
  pipeline: "Pipeline goal hit — fresh leads on the board!",
  wins: "Win logged — daily goal complete!",
};

const PERIOD_LABELS: Record<GamificationPeriodKey, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

type Crossing = { period: GamificationPeriodKey; metric: GamificationMetricKey };

// Cross-tab dedupe + per-metric cooldown: when the same user has multiple
// tabs open, only one tab should celebrate a given goal hit. We coordinate
// via the Web Locks API (atomic claim across same-origin tabs) plus a
// localStorage "recently celebrated" record so a tab that polls the snapshot
// after another tab already celebrated doesn't re-fire.
//
// The same record also acts as a per-metric cooldown: if a daily goal count
// flickers across the 100% line (e.g. an activity is logged, removed, then
// re-logged) we don't re-celebrate the same metric back-to-back. Each
// (period, metric, periodStart) bucket is celebrated at most once per
// cooldown window, so a different metric crossing or the next day's bucket
// still celebrates immediately.
// Exported for unit tests so assertions can reference the canonical key
// prefixes / window sizes instead of duplicating string literals (which would
// silently drift if they ever change).
export const CELEBRATION_LOCK_PREFIX = "gamification-celebration-lock:";
export const CELEBRATION_LS_PREFIX = "gamification:celebrated:";
// 5-minute window suppresses both cross-tab races and rapid re-crossings of
// the same daily metric. Long enough to absorb edit/undo/redo flickers near
// the goal boundary, short enough that stale entries don't pile up
// (one record per period+metric+bucket, naturally expiring as new buckets
// arrive).
export const CELEBRATION_TTL_MS = 5 * 60_000;
// How long the winning tab holds the lock so concurrent claimants see it busy
// and bail out instead of also celebrating.
const CELEBRATION_LOCK_HOLD_MS = 400;
// Foreground bias: hidden tabs wait briefly before trying to claim, so a
// visible tab (which races first) wins the lock. If there's no visible tab,
// whichever tab gets there first still celebrates.
const HIDDEN_TAB_DELAY_MS = 250;

// Exported for unit tests in global-celebrations.test.tsx so the dedupe
// helpers can be exercised in isolation without spinning up the full
// component / React Query plumbing.
export function buildCelebrationKey(snapshot: GamificationSnapshot, crossing: Crossing): string {
  // Bucket per period so the same logical "Weekly activity goal hit during
  // week of <start>" produces the same key in every tab. snapshot[period].start
  // is the canonical bucket boundary the backend reports for each period, so
  // dedupe works across reloads within the period.
  const periodStart = snapshot[crossing.period]?.start ?? snapshot.date;
  return `${crossing.period}:${crossing.metric}:${periodStart}`;
}

export function wasRecentlyCelebrated(key: string): boolean {
  try {
    const raw = window.localStorage.getItem(CELEBRATION_LS_PREFIX + key);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    if (Date.now() - ts > CELEBRATION_TTL_MS) {
      window.localStorage.removeItem(CELEBRATION_LS_PREFIX + key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function recordCelebrated(key: string): void {
  try {
    window.localStorage.setItem(CELEBRATION_LS_PREFIX + key, String(Date.now()));
  } catch {
    // Storage may be unavailable (private mode, quota); fall back to lock-only dedupe.
  }
}

export async function claimCelebration(lockKey: string, recordKeys: string[]): Promise<boolean> {
  // If any of the contributing celebration keys was already claimed recently
  // by another tab, treat this batch as already handled.
  if (recordKeys.some(wasRecentlyCelebrated)) return false;

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== "function") {
    // Older browser: best-effort dedupe via localStorage timestamp only. Re-check
    // after a tiny jitter to reduce the chance of two tabs racing past the check.
    if (recordKeys.some(wasRecentlyCelebrated)) return false;
    recordKeys.forEach(recordCelebrated);
    return true;
  }

  let claimed = false;
  try {
    await locks.request(
      CELEBRATION_LOCK_PREFIX + lockKey,
      { ifAvailable: true },
      async (lock) => {
        // Lock is null when another tab is currently holding it — that tab is
        // about to celebrate (or just did), so we bow out.
        if (!lock) return;
        if (recordKeys.some(wasRecentlyCelebrated)) return;
        recordKeys.forEach(recordCelebrated);
        claimed = true;
        // Hold the lock briefly so other tabs that race in immediately after
        // see it as busy and skip. Once we release it, the localStorage TTL
        // takes over for late-arriving tabs.
        await new Promise<void>((resolve) => setTimeout(resolve, CELEBRATION_LOCK_HOLD_MS));
      },
    );
  } catch {
    // If the locks API throws (e.g. document destroyed mid-request), assume we
    // didn't celebrate and let another tab handle it.
    return false;
  }
  return claimed;
}

/**
 * Mounts once for an authenticated user and listens to the gamification
 * snapshot. When any daily/weekly/monthly metric crosses its goal
 * (pct < 1 -> pct >= 1) the user gets a brief celebration
 * (toast + optional confetti + optional sound) regardless of which page
 * they're on.
 *
 * Tiering — bigger fanfare for longer-window milestones:
 *   - daily   -> regular toast + standard confetti + goal/win sound
 *   - weekly  -> larger toast + larger confetti + win sound
 *   - monthly -> longest toast + huge confetti + extended fanfare sound
 *
 * If multiple periods/metrics cross at once they are combined into a single,
 * clearer toast (no stacking) at the highest tier that crossed.
 *
 * Honors:
 *   - silentMode: suppress sounds (toast + confetti still fire)
 *   - reduceMotion: suppress confetti (toast + sound still fire)
 *
 * Cross-tab: when the same user has multiple tabs open we only celebrate in
 * one (preferring a visible tab). The other tabs still receive the snapshot
 * update, so widgets stay in sync visually — they just don't re-fire the
 * confetti/toast/sound.
 *
 * Note: milestone detection is intentionally only done here so that mounting
 * the dashboard widget at the same time does not double-fire the celebration.
 */
type PopupState = Omit<GoalAchievementPopupProps, "onClose">;

export function GlobalGamificationCelebrations() {
  const [popup, setPopup] = useState<PopupState | null>(null);

  const { data: snapshot } = useQuery<GamificationSnapshot>({
    queryKey: ["/api/gamification/snapshot"],
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // Track the previously seen snapshot so we can detect *crossings* (not just
  // "currently >= 100%"), which prevents firing the celebration on first load
  // for goals already hit earlier in the period.
  const prevSnapRef = useRef<GamificationSnapshot | null>(null);

  useEffect(() => {
    if (!snapshot || !snapshot.enabled) {
      prevSnapRef.current = snapshot ?? null;
      return;
    }

    const prev = prevSnapRef.current;
    // Update the baseline up front so a re-render mid-claim doesn't re-detect
    // the same crossing.
    prevSnapRef.current = snapshot;

    // First-ever load: just remember the baseline. We don't want to congratulate
    // the user for goals they hit before opening the app.
    if (!prev) {
      return;
    }

    const allCrossings: Crossing[] = [];
    for (const p of PERIOD_KEYS) {
      for (const k of METRIC_KEYS) {
        const before = prev[p][k];
        const after = snapshot[p][k];
        if (before.pct < 1 && after.pct >= 1) {
          allCrossings.push({ period: p, metric: k });
        }
      }
    }

    if (allCrossings.length === 0) return;

    // Filter out crossings still inside their per-metric cooldown (or already
    // celebrated by another tab). Each crossing is judged independently so a
    // fresh crossing for one metric isn't suppressed just because another
    // metric in the same snapshot tick is in cooldown — e.g. daily activity
    // re-crosses (cooled down) while daily wins crosses for the first time;
    // wins should still celebrate.
    const crossings = allCrossings.filter(
      (c) => !wasRecentlyCelebrated(buildCelebrationKey(snapshot, c)),
    );
    if (crossings.length === 0) return;

    // Determine highest tier that crossed (monthly > weekly > daily).
    const hasMonthly = crossings.some((c) => c.period === "monthly");
    const hasWeekly = crossings.some((c) => c.period === "weekly");
    const tier: GamificationPeriodKey = hasMonthly
      ? "monthly"
      : hasWeekly
        ? "weekly"
        : "daily";

    let cancelled = false;
    void (async () => {
      // Bias the lock race toward the foreground tab: hidden tabs wait a beat
      // so a visible tab (if any) claims first.
      if (typeof document !== "undefined" && document.hidden) {
        await new Promise<void>((resolve) => setTimeout(resolve, HIDDEN_TAB_DELAY_MS));
        if (cancelled) return;
      }

      const recordKeys = crossings.map((c) => buildCelebrationKey(snapshot, c));
      // The lock name combines all fresh crossings in this batch so a single
      // tick is claimed atomically — two tabs can't each grab a different
      // metric and double-fire pieces of the same celebration.
      const lockKey = recordKeys.join("|");

      const ok = await claimCelebration(lockKey, recordKeys);
      if (!ok || cancelled) return;

      // Sound: respects soundsAllowed (which already reflects silentMode).
      if (snapshot.soundsAllowed) {
        if (tier === "monthly") {
          playBigWinSound();
        } else if (tier === "weekly") {
          playWinSound();
        } else {
          // Daily — preserve prior behavior: win sound if 'wins' crossed,
          // otherwise the gentler goal arpeggio.
          const dailyWinsCrossed = crossings.some(
            (c) => c.period === "daily" && c.metric === "wins",
          );
          if (dailyWinsCrossed) playWinSound();
          else playGoalSound();
        }
      }

      // Confetti: respects reduceMotion. Bigger windows get bigger bursts.
      if (!snapshot.reduceMotion) {
        if (tier === "monthly") fireConfetti({ intensity: "huge" });
        else if (tier === "weekly") fireConfetti({ intensity: "large" });
        else fireConfetti();
      }

      // Popup: replaces the corner toast — visually prominent, names the goal.
      const { title, description, duration } = buildToastContent(crossings, tier);
      const primaryMetric = crossings.length === 1 ? crossings[0].metric : undefined;
      setPopup({ title, description, tier, metric: primaryMetric, duration });
    })();

    return () => {
      cancelled = true;
    };
  }, [snapshot]);

  // ──────────────────────────────────────────────────────────────────────
  // Coach Mode v2 (Task #311) — BONUS confetti when a quantified aspiration
  // bar crosses 100%. This runs as its own effect (independent of the metric
  // celebration above) so that when a single booking both hits a daily win
  // goal AND completes a coach aspiration, BOTH celebrations fire and the
  // confetti stacks. Reuses the same cross-tab claim + cooldown machinery so
  // multiple open tabs don't double-fire.
  const prevAspRef = useRef<Map<string, number> | null>(null);
  useEffect(() => {
    if (!snapshot || !snapshot.enabled) {
      // Keep aspiration baseline in sync even while metric celebrations are
      // disabled, so we don't fire a stale crossing when it re-enables.
      prevAspRef.current = snapshotAspMap(snapshot);
      return;
    }
    const prev = prevAspRef.current;
    const curr = snapshotAspMap(snapshot);
    prevAspRef.current = curr;
    if (!prev) return; // first load — establish baseline only

    const completedNow: { id: string; title: string }[] = [];
    for (const a of snapshot.coachAspirations ?? []) {
      const before = prev.get(a.id);
      if (before == null) continue; // newly appeared — don't celebrate retroactively
      if (before < 1 && a.pct >= 1) completedNow.push({ id: a.id, title: a.title });
    }
    if (completedNow.length === 0) return;

    let cancelled = false;
    void (async () => {
      if (typeof document !== "undefined" && document.hidden) {
        await new Promise<void>((resolve) => setTimeout(resolve, HIDDEN_TAB_DELAY_MS));
        if (cancelled) return;
      }
      const recordKeys = completedNow.map(
        (c) => `coach-aspiration:${c.id}:${snapshot.date}`,
      );
      const ok = await claimCelebration(recordKeys.join("|"), recordKeys);
      if (!ok || cancelled) return;

      if (snapshot.soundsAllowed) playBigWinSound();
      // Bonus confetti — huge burst, stacking on top of any metric confetti
      // that fired in the same tick.
      if (!snapshot.reduceMotion) fireConfetti({ intensity: "huge" });

      const title =
        completedNow.length === 1
          ? "Goal achieved! 🎉"
          : `${completedNow.length} goals achieved! 🎉`;
      const description =
        completedNow.length === 1
          ? `You reached: ${completedNow[0].title}`
          : completedNow.map((c) => c.title).join(" • ");
      setPopup({ title, description, tier: "aspiration", duration: 7000 });
    })();

    return () => {
      cancelled = true;
    };
  }, [snapshot]);

  return popup ? (
    <GoalAchievementPopup {...popup} onClose={() => setPopup(null)} />
  ) : null;
}

// Build an id -> pct map for the snapshot's coach aspirations, used to detect
// 100% crossings between snapshot ticks.
function snapshotAspMap(
  snapshot: GamificationSnapshot | null | undefined,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const a of snapshot?.coachAspirations ?? []) m.set(a.id, a.pct);
  return m;
}

function buildToastContent(
  crossings: Crossing[],
  tier: GamificationPeriodKey,
): { title: string; description: string; duration: number } {
  // Single crossing — keep messaging concise and metric-specific.
  if (crossings.length === 1) {
    const c = crossings[0];
    if (c.period === "daily") {
      return {
        title: `${METRIC_LABELS[c.metric]} goal hit!`,
        description: DAILY_GOAL_HIT_MESSAGES[c.metric],
        duration: 4000,
      };
    }
    if (c.period === "weekly") {
      return {
        title: "Weekly milestone reached!",
        description: `Weekly ${METRIC_LABELS[c.metric].toLowerCase()} goal complete — sustained performance pays off.`,
        duration: 5500,
      };
    }
    return {
      title: "Monthly milestone reached!",
      description: `Monthly ${METRIC_LABELS[c.metric].toLowerCase()} goal complete — a full month of crushing it.`,
      duration: 7000,
    };
  }

  // Multiple crossings — combine into one clearer toast, grouped by period
  // so the highest-impact wins read first.
  const grouped: string[] = [];
  for (const p of ["monthly", "weekly", "daily"] as GamificationPeriodKey[]) {
    const metrics = crossings
      .filter((c) => c.period === p)
      .map((c) => METRIC_LABELS[c.metric]);
    if (metrics.length === 0) continue;
    grouped.push(`${PERIOD_LABELS[p]}: ${metrics.join(", ")}`);
  }
  const description = `${grouped.join(" • ")} — incredible work!`;

  if (tier === "monthly") {
    return { title: "Monthly milestone reached!", description, duration: 7000 };
  }
  if (tier === "weekly") {
    return { title: "Weekly milestone reached!", description, duration: 5500 };
  }
  return { title: "Multiple goals hit!", description, duration: 4500 };
}
